import type { CustomInstrumentInfo } from "@repo/shared/types";
import { LineChart, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./custom-instruments-table.i18n";

type CustomInstrumentTableProps = {
  items: CustomInstrumentInfo[];
  onEdit: (item: CustomInstrumentInfo) => void;
  onDelete: (item: CustomInstrumentInfo) => void;
  onNameClick: (item: CustomInstrumentInfo) => void;
};

export function CustomInstrumentTable({
  items,
  onEdit,
  onDelete,
  onNameClick,
}: CustomInstrumentTableProps) {
  const t = useT(T);

  if (items.length === 0) {
    return <EmptyState>{t.noData}</EmptyState>;
  }

  return (
    <Table variant="data">
      <TableHeader>
        <TableRow>
          <TableHead className="w-40">{t.name}</TableHead>
          <TableHead className="w-40">{t.ticker}</TableHead>
          <TableHead className="w-20">{t.currency}</TableHead>
          <TableActionsHead>{t.actions}</TableActionsHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.ticker}>
            <TableCell className="font-medium">{item.name}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{item.ticker}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{item.currency}</TableCell>
            <TableActionsCell>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onNameClick(item)}
                tooltip={t.viewChart}
              >
                <LineChart className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onEdit(item)} tooltip={t.edit}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onDelete(item)} tooltip={t.delete}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </TableActionsCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
